import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base, PlusIcon } from '@itsrever/design-system'
import { ShippingConfiguration } from '@/hooks'
import { ShippingRule } from '@/application/manage-shipping'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { useCountries } from '@/application/queries/use-countries'
import { EU_ALIAS_NAME, isEuCountry } from '@/domain/countries'
import {
  AddCondition,
  OpenKey,
  RuleListFrame,
  RuleRow,
  SetOpenKey,
  bindOpen,
  newRuleKey,
  useNewRuleShortcut,
  useRuleOrder
} from '@/ui/components/rule-list'
import { ConditionChip, comparisonsOf, useFieldGroups } from './condition-chip'
import { CostCell } from './cost-cell'
import {
  Draft,
  DraftRule,
  TestReturn,
  newCondition,
  runTest,
  statusOf
} from './shipping-rules'
import { TestPanel, newTestReturn } from './test-panel'

const { Button, Callout } = Base

interface RuleTableProps {
  draft: Draft
  stored: ShippingRule[]
  availableConfiguration: ShippingConfiguration
  editable: boolean
  // Extra toolbar actions, e.g. editing with the assistant.
  actions?: ReactNode
  onChange: (draft: Draft) => void
}

export const RuleTable: React.FC<RuleTableProps> = ({
  draft,
  stored,
  availableConfiguration,
  editable,
  actions,
  onChange
}) => {
  const { t } = useTranslation()
  const { countries } = useCountries()
  const [openKey, setOpenKey] = useState<OpenKey>(null)
  const [test, setTest] = useState<TestReturn | null>(null)
  const { rules, fallback } = draft

  const setRules = (next: DraftRule[]) => onChange({ ...draft, rules: next })
  const update = (rule: DraftRule) =>
    setRules(rules.map((r) => (r.key === rule.key ? rule : r)))
  const rowProps = useRuleOrder(rules, setRules)
  const showDialog = useDialog()
  const remove = async (rule: DraftRule) => {
    const confirmed = await showDialog({
      title: t('edit_shipping.delete_rule'),
      message: t('edit_shipping.are_you_sure_delete_rule'),
      okButtonText: t('edit_shipping.yes_delete'),
      cancelButtonText: t('save_bar.cancel'),
      destructive: true
    })
    if (confirmed) setRules(rules.filter((r) => r.key !== rule.key))
  }
  const addRule = () => {
    const key = newRuleKey()
    setRules([
      ...rules,
      {
        key,
        params: { cost: '0' },
        returnOriginalShippingCosts: false,
        conditions: []
      }
    ])
    setOpenKey(`${key}:add`)
  }
  useNewRuleShortcut(editable && !openKey, addRule)

  // Rules hold country names; settings-api's "EU" alias covers its member states.
  const inGroup = (country: string, value: string) =>
    value === EU_ALIAS_NAME &&
    isEuCountry(countries.find((c) => c.name === country)?.iso ?? '')
  const { matched, skipped } = test
    ? runTest(rules, test, inGroup)
    : { matched: -1, skipped: [] }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-neutral-500">
          {t('shipping_rules.rule_count', { count: rules.length })}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setTest(
                test ? null : newTestReturn(rules, availableConfiguration)
              )
            }
          >
            {t(
              test ? 'shipping_rules.close_test' : 'shipping_rules.test_return'
            )}
          </Button>
          {editable ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addRule}
            >
              <PlusIcon color="currentColor" className="size-4" />
              {t('shipping_rules.add_rule')}
            </Button>
          ) : null}
          {actions}
        </div>
      </div>
      {test ? (
        <TestPanel
          test={test}
          rules={rules}
          availableConfiguration={availableConfiguration}
          result={
            matched >= 0
              ? {
                  cost: rules[matched].params.cost,
                  returnOriginalShippingCosts:
                    rules[matched].returnOriginalShippingCosts
                }
              : fallback
          }
          resultRule={matched}
          skipped={skipped}
          onChange={setTest}
          onClose={() => setTest(null)}
        />
      ) : null}
      <Callout tone="info" role="note" className="px-4 py-3 text-xs">
        <span className="font-medium text-neutral-900">
          {t('shipping_rules.order_matters')}
        </span>{' '}
        <Trans
          i18nKey="shipping_rules.order_matters_example"
          components={{ i: <i /> }}
        />
      </Callout>
      <RuleListFrame
        ns="shipping_rules"
        outcome={{
          label: t('shipping_rules.customer_pays'),
          className: 'w-48 text-right'
        }}
        fallbackHighlighted={!!test && matched < 0}
        fallback={
          <CostCell
            id="fallback"
            value={fallback}
            label={t('shipping_rules.fallback_cost_label')}
            editable={editable}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onChange={(cost) =>
              onChange({ ...draft, fallback: { ...fallback, ...cost } })
            }
          />
        }
      >
        {rules.map((rule, index) => (
          <RuleRow
            key={rule.key}
            {...rowProps(rule, index)}
            ns="shipping_rules"
            status={statusOf(rule, stored)}
            highlighted={!!test && index === matched}
            dimmed={!!test && matched >= 0 && index > matched}
            editable={editable}
            outcome={
              <CostCell
                id={rule.key}
                value={{
                  cost: rule.params.cost,
                  returnOriginalShippingCosts: rule.returnOriginalShippingCosts
                }}
                label={t('shipping_rules.cost_label', { n: index + 1 })}
                editable={editable}
                openKey={openKey}
                setOpenKey={setOpenKey}
                onChange={({ cost, returnOriginalShippingCosts }) =>
                  update({
                    ...rule,
                    params: { cost },
                    returnOriginalShippingCosts
                  })
                }
              />
            }
            onDelete={() => remove(rule)}
          >
            <RuleConditions
              rule={rule}
              n={index + 1}
              availableConfiguration={availableConfiguration}
              editable={editable}
              openKey={openKey}
              setOpenKey={setOpenKey}
              onChange={update}
            />
          </RuleRow>
        ))}
      </RuleListFrame>
      {editable ? (
        <p className="text-xs text-neutral-400">{t('shipping_rules.hint')}</p>
      ) : null}
    </div>
  )
}

const RuleConditions: React.FC<{
  rule: DraftRule
  n: number
  availableConfiguration: ShippingConfiguration
  editable: boolean
  openKey: OpenKey
  setOpenKey: SetOpenKey
  onChange: (rule: DraftRule) => void
}> = ({
  rule,
  n,
  availableConfiguration,
  editable,
  openKey,
  setOpenKey,
  onChange
}) => {
  const { t } = useTranslation()
  const fieldGroups = useFieldGroups(availableConfiguration)
  const setConditions = (conditions: ShippingRule['conditions']) =>
    onChange({ ...rule, conditions })

  return (
    <>
      {rule.conditions.map((condition, i) => (
        <span key={i} className="inline-flex max-w-full items-center gap-2">
          {i > 0 ? (
            <span className="text-xs text-neutral-400">
              {t('shipping_rules.and')}
            </span>
          ) : null}
          <ConditionChip
            id={`${rule.key}-c${i}`}
            condition={condition}
            availableConfiguration={availableConfiguration}
            editable={editable}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onChange={(next) =>
              setConditions(rule.conditions.map((c, k) => (k === i ? next : c)))
            }
            onRemove={() =>
              setConditions(rule.conditions.filter((_, k) => k !== i))
            }
          />
        </span>
      ))}
      {editable ? (
        <AddCondition
          ns="shipping_rules"
          n={n}
          empty={rule.conditions.length === 0}
          groups={fieldGroups}
          searchLabel={t('shipping_rules.search_conditions')}
          emptyText={t('shipping_rules.no_field')}
          width="w-72"
          {...bindOpen(`${rule.key}:add`, openKey, setOpenKey)}
          onPick={(fn) => {
            const condition = newCondition(
              fn,
              comparisonsOf(fn, availableConfiguration)
            )
            setConditions([...rule.conditions, condition])
            setOpenKey(
              condition.values.length
                ? null
                : `${rule.key}-c${rule.conditions.length}:value`
            )
          }}
        />
      ) : null}
    </>
  )
}
